import * as React from "react";
import { useState, useEffect, useContext } from "react";
import {
  useListContext,
  DataProviderContext,
  useRefresh,
  useGetList,
  useNotify,
  Confirm,
} from "react-admin";
import { Box, Typography } from "@mui/material";
import { DragDropContext } from "@hello-pangea/dnd";
import isEqual from "lodash/isEqual";
import { stages } from "./stages";
import PurchaseColumn from "./PurchaseColumn";

const initialPurchases = stages.reduce(
  (obj, stage) => ({ ...obj, [stage]: [] }),
  {}
);

const getPurchasesByColumn = (data) => {
  // Group purchases by column
  const columns = data?.reduce(
    (acc, record) => {
      acc[record.status].push(record.id);
      return acc;
    },
    stages.reduce((obj, stage) => ({ ...obj, [stage]: [] }), {})
  );

  // Order each column by index
  stages?.forEach((stage) => {
    columns[stage] = columns[stage].sort(
      (a, b) =>
        data.find((d) => d._id === a).updatedAt -
        data.find((d) => d._id === b).updatedAt
    );
  });
  return columns;
};

const PurchaseKanbanContent = () => {
  const { filterValues, setFilters } = useListContext();
  const refresh = useRefresh();
  const notify = useNotify();
  const dataProvider = useContext(DataProviderContext);
  const [showConfirm, setShowConfirm] = useState(false);
  const [purchaseToDeliver, setPurchaseToDeliver] = useState();
  const [dragResult, setDragResult] = useState();

  const { data, isPending } = useGetList("purchases", {
    pagination: { page: 1, perPage: 10000 }, // FIXME: get all pages at once?!
    sort: { field: "paidDate", order: "DESC" },
    filter: filterValues,
  });

  const [purchases, setPurchases] = useState(
    !isPending ? getPurchasesByColumn(data) : initialPurchases
  );

  useEffect(() => {
    if (isPending) return;
    const newPurchases = getPurchasesByColumn(data);
    if (isEqual(purchases, newPurchases)) {
      return;
    }
    setPurchases(newPurchases);
  }, [data, isPending]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDialogClose = () => {
    const { destination, source, draggableId } = dragResult;
    const sourceColumn = Array.from(purchases[source.droppableId]);
    const destinationColumn = Array.from(purchases[destination.droppableId]);

    // Revert local state
    sourceColumn.splice(source.index, 0, draggableId);
    destinationColumn.splice(destination.index, 1);

    setPurchases({
      ...purchases,
      [source.droppableId]: sourceColumn,
      [destination.droppableId]: destinationColumn,
    });

    setShowConfirm(false);
    setPurchaseToDeliver(null);
    setDragResult(null);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    if (!purchaseToDeliver) return;

    // Deliver purchase
    try {
      const deliveredPurchase = await dataProvider.deliverPurchase(
        "purchases",
        { id: purchaseToDeliver._id }
      );
      if (deliveredPurchase) {
        notify("¡Pedido entregado con éxito!", {
          type: "success",
        });
        setFilters({ ...filterValues, deliveryCode: "" }, [], true);
      }
    } catch (error) {
      notify("Ha ocurrido un error al entregar el pedido", { type: "error" });
    } finally {
      setPurchaseToDeliver(null);
      setDragResult(null);
      refresh();
    }
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // Disable moving from Delivered to Paid
    if (
      !destination ||
      (source.droppableId === "DELIVERED" && destination.droppableId === "PAID")
    ) {
      return;
    }

    // Disable moving in the same column
    if (source.droppableId === destination.droppableId) return;

    // Validate delivery code
    if (!filterValues.deliveryCode) {
      notify(
        "Escribe el código de 6 dígitos que te indique el cliente (paso 1) antes de pasar un pedido de Recibido a Entregado.",
        {
          type: "error",
        }
      );
      return;
    }

    const sourceColumn = Array.from(purchases[source.droppableId]);
    const destinationColumn = Array.from(purchases[destination.droppableId]);
    const sourcePurchase = data.find(
      (d) => d._id === sourceColumn[source.index]
    );

    // Update local state
    sourceColumn.splice(source.index, 1);
    destinationColumn.splice(destination.index, 0, draggableId);
    setPurchases({
      ...purchases,
      [source.droppableId]: sourceColumn,
      [destination.droppableId]: destinationColumn,
    });

    setShowConfirm(true);
    setDragResult(result);
    setPurchaseToDeliver(sourcePurchase);
  };

  if (isPending) return null;

  return (
    <>
      <h3 style={{ marginLeft: 16, paddingTop: 16 }}>
        2. <b>Arrastra</b> el pedido de Recibido a Entregado, o presiona el
        botón <b>ENTREGAR</b>:
      </h3>
      <Typography color="error" sx={{ ml: 2 }}>
        No olvides <b>marcar el pedido como entregado</b> para que el dinero se
        acredite en tu cuenta.
      </Typography>
      <DragDropContext onDragEnd={onDragEnd}>
        <Box style={{ display: "flex", flexDirection: "row" }}>
          {stages.map((stage) => (
            <PurchaseColumn
              stage={stage}
              purchaseIds={purchases[stage]}
              data={data}
              key={stage}
              handleDeliverClick={(id) => {
                if (!filterValues.deliveryCode) {
                  notify(
                    "Escribe el código de 6 dígitos que te indique el cliente (paso 1) antes de pasar un pedido de Recibido a Entregado.",
                    {
                      type: "error",
                    }
                  );
                  return;
                }
                setPurchaseToDeliver(data.find((d) => d._id === id));
                setShowConfirm(true);
              }}
            />
          ))}
        </Box>
      </DragDropContext>
      <Confirm
        isOpen={showConfirm}
        loading={isPending}
        title={`Entregar pedido`}
        content="¿Estás seguro de entregar este pedido?"
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};

export default PurchaseKanbanContent;
